import React, { useState, useEffect } from "react";
import { Avatar } from "primereact/avatar";
import { Button } from "primereact/button";
import { CircleUserRound, Copy, SquareArrowOutUpRight, LoaderCircle, CircleCheckBig, BookCopy, ThumbsUp, ThumbsDown, SquareMousePointer, ChevronUp, ExternalLink, ChevronRight, ChevronLeft, RefreshCw } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./index.css";
import FeedbackDialog from "./FeedbackDialog";
import { Tooltip as MatToolTip } from "@mui/material";
import qodexLogoFull from '../../assets/logos/logo_small.png';

// Helper function to extract domain and category from URL
const getSourceInfo = (url) => {
    try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname;
        const path = urlObj.pathname;

        // Extract slug as category (remove leading slash and convert to title case)
        const slug = path.replace(/^\//, '').replace(/-/g, ' ');
        const words = slug.split(' ').slice(0, 3); // Get first three words only
        const category = words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') + (slug.split(' ').length > 3 ? '...' : '');

        return { domain, category, url };
    } catch (error) {
        return { domain: "docs.qodex.ai", category: "Documentation", url };
    }
};

const DefaultMessageResponse = ({ latestMessageId, message, index, handleCopy, handleOpenTab, chatId, onSuggestionClick, isNewTabOpened, setIsNewTabOpened, handleTryAgain }) => {
    const isUserMessage = message.message_type === "user_message";
    const isAIMessage = message.message_type === "ai_message" || message.message_type === "system_message";
    const isLoading = message.loading;
    const hasTestScenario = message.generated_test_scenarios && message.generated_test_scenarios.length > 0;
    const hasSelectedCollection = message.selected_collection !== undefined;
    const hasCurrentTestScenario = message.current_test_scenario !== null;
    const hasCurrentTestCaseSet = message.current_test_case_set !== null;
    const hasSuggestions = message.suggestions && message.suggestions.length > 0;
    const [feedBackDialogVisible, setFeedBackDialogVisible] = useState(false);
    const [selectedMessageId, setSelectedMessageId] = useState(null);
    const [feedbackType, setFeedbackType] = useState(null);
    const [showFullContent, setShowFullContent] = useState(false);
    const isLatestMessage = message.id === latestMessageId;
    const hasProcessedEntityCount = message.processed_entity_count !== undefined;
    const hasTotalEntityCount = message.total_entity_count !== undefined;
    const hasSources = message.sources && message.sources.length > 0;

    useEffect(() => {
        if (!isNewTabOpened) {
            // Check for selected collection
            if (hasSelectedCollection) {
                setIsNewTabOpened(true);
                handleOpenTab({
                    icon: <BookCopy size={16} className="text-indigo-400" />,
                    title: message.selected_collection.name,
                    subtitle: "Swagger Doc",
                    type: "collection-swagger",
                    id: message.selected_collection.id,
                }, "collection-swagger");
                return;
            }

            // Check for test scenarios
            if (hasTestScenario) {
                const firstNonLoadingScenario = message.generated_test_scenarios.find(scenario => !scenario.loading);
                if (firstNonLoadingScenario) {
                    setIsNewTabOpened(true);
                    handleOpenTab({
                        title: firstNonLoadingScenario.title,
                        type: "test-scenario",
                        id: firstNonLoadingScenario.id,
                    }, "test-scenario");
                    return;
                }
            }

            // Check for current test scenario
            if (hasCurrentTestScenario) {
                setIsNewTabOpened(true);
                handleOpenTab({
                    title: message.current_test_scenario.title,
                    type: "test-scenario",
                    id: message.current_test_scenario.test_scenario_id,
                }, "test-scenario");
                return;
            }

            // Check for current test case set
            if (hasCurrentTestCaseSet) {
                setIsNewTabOpened(true);
                handleOpenTab({
                    title: message.current_test_case_set.title,
                    type: "test-scenario",
                    id: message.current_test_case_set.test_scenario_id,
                    testCaseSetId: message.current_test_case_set.test_case_set_id,
                }, "test-scenario");
                return;
            }
        }
    }, [message, isNewTabOpened]);

    const showFeedbackDialog = (messageId, feedbackType) => {
        setFeedBackDialogVisible(true);
        setSelectedMessageId(messageId);
        setFeedbackType(feedbackType);
    };

    const hideFeedbackDialog = () => {
        setFeedBackDialogVisible(false);
        setSelectedMessageId(null);
        setFeedbackType(null);
    };

    return (
        <>
            {isUserMessage && (
                <div key={message.id || index} className="flex justify-end mb-3">
                    <div
                        className="max-w-[85%] shadow-xl transition-all duration-200 rounded-md px-2 py-1 bg-[var(--primary-color)] text-white relative"
                    >
                        <pre className="m-0 leading-relaxed whitespace-pre-wrap break-words !custom_font" style={{ fontSize: "13px", fontFamily: "Geist Sans" }}>
                            {message.message_content}
                        </pre>
                    </div>

                    <Avatar
                        icon={<CircleUserRound size={14} />}
                        className="ml-1 bg-[var(--primary-color)] text-white shadow-lg"
                        style={{ width: "18px", height: "18px" }}
                        shape="circle"
                    />
                </div>
            )}

            {isAIMessage && (
                <div key={message.id} className="flex justify-start mb-3">
                    {/* <Avatar
                        icon={<img src={qodexLogoFull} alt="Qodex AI Assistant" className="object-cover w-full h-full" />}
                        className="mr-3 bg-white text-[var(--text-primary)] shadow-lg"
                        style={{ width: "2.5rem", height: "2.5rem" }}
                        shape="circle"
                    /> */}

                    <div
                        className={`group w-[95%] transition-all duration-200 rounded-md px-2 py-1 text-[var(--text-primary)] relative mb-4`} style={{ fontSize: "13px !important" }}>
                        <ReactMarkdown children={message.message_content} remarkPlugins={[remarkGfm]} className="markdown-content text-sm m-0 leading-relaxed break-words" />
                        {/* Progress Bar */}
                        {hasProcessedEntityCount && hasTotalEntityCount && (
                            <div className="mt-2">
                                <div className="flex justify-between items-center text-xs text-[var(--text-secondary)] mb-1">
                                    <span>
                                        {message.processed_entity_count === message.total_entity_count
                                            ? "Processing completed!"
                                            : "Processing APIs..."}
                                    </span>
                                    <span>{message.processed_entity_count} / {message.total_entity_count}</span>
                                </div>
                                <div className="w-full bgSecondary rounded-full h-2">
                                    <div
                                        className={`h-2 rounded-full transition-all duration-300 ${message.processed_entity_count === message.total_entity_count
                                            ? "bg-green-500"
                                            : "bg-[var(--primary-color)]"
                                            }`}
                                        style={{
                                            width: `${(message.processed_entity_count / message.total_entity_count) * 100}%`,
                                            maxWidth: "100% !important"
                                        }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="absolute -bottom-8 left-0 flex -my-2 opacity-100 group-hover:!opacity-100 transition-opacity duration-150 w-full">
                            <MatToolTip title="Good Response" placement="bottom">
                                <Button
                                    type="button"
                                    onClick={() => showFeedbackDialog(message.id, "positive")}
                                    aria-label="Feedback"
                                    className="w-auto h-8 bg-transparent hover:bg-[var(--button-secondary-hover)] border-none text-[var(--text-secondary)] px-2 py-1 m-1 !rounded shadow-none transition-all duration-150"
                                >
                                    <ThumbsUp
                                        size={16}
                                        className="cursor-pointer transition-transform duration-300 hover:rotate-[-18deg]"
                                    />
                                </Button>
                            </MatToolTip>

                            <MatToolTip title="Bad Response" placement="bottom">
                                <Button
                                    type="button"
                                    onClick={() => showFeedbackDialog(message.id, "negative")}
                                    aria-label="Feedback"
                                    className="w-auto h-8 bg-transparent hover:bg-[var(--button-secondary-hover)] border-none text-[var(--text-secondary)] px-2 py-1 m-1 !rounded shadow-none transition-all duration-150"
                                >
                                    <ThumbsDown
                                        size={16}
                                        className="cursor-pointer transition-transform duration-300 hover:rotate-[-18deg]"
                                    />
                                </Button>
                            </MatToolTip>

                            <MatToolTip title="Copy" placement="bottom">
                                <Button
                                    type="button"
                                    onClick={() => handleCopy(message.message_content)}
                                    aria-label="Copy"
                                    className="w-auto h-8 bg-transparent hover:bg-[var(--button-secondary-hover)] border-none text-[var(--text-secondary)] px-2 py-1 m-1 !rounded shadow-none transition-all duration-150"
                                >
                                    <Copy
                                        size={16}
                                        className="cursor-pointer transition-transform duration-500 hover:scale-110"
                                    />
                                </Button>
                            </MatToolTip>
                            {isLatestMessage && (
                                <MatToolTip title="Retry" placement="bottom">
                                    <Button
                                        type="button"
                                        onClick={() => handleTryAgain()}
                                        aria-label="Retry"
                                        className="w-auto h-8 bg-transparent hover:bg-[var(--button-secondary-hover)] border-none text-[var(--text-secondary)] px-2 py-1 m-1 !rounded shadow-none transition-all duration-150"
                                    >
                                        <RefreshCw
                                            size={16}
                                            className="cursor-pointer transition-transform duration-500 hover:rotate-90"
                                        />
                                    </Button>
                                </MatToolTip>
                            )}
                        </div>

                        {hasTestScenario && (
                            <div className="mt-4">
                                <hr className="border-[var(--borderColor)]" />
                                <div className="grid grid-cols-1 gap-3">
                                    {message.generated_test_scenarios.map((scenario, index) => (
                                        <div key={scenario.id} className={`bg-[var(--agent-option)] hover:bg-[var(--hover-bg)] px-2 py-1 rounded-md text-sm font-semibold shadow-md transition-all`}>
                                            <div className="flex justify-between items-center gap-x-4">
                                                <div className="flex items-center gap-x-2 min-w-0"
                                                    onClick={() => {
                                                        if (!scenario.loading) {
                                                            handleOpenTab(scenario, "test-scenario");
                                                        }
                                                    }}>
                                                    {!scenario.loading && (
                                                        <>
                                                            <MatToolTip title="Open Test Scenario" placement="bottom">
                                                                <div
                                                                    className="text-[var(--text-primary)] cursor-pointer ml-2"
                                                                >
                                                                    <SquareArrowOutUpRight size={16} />
                                                                </div>
                                                            </MatToolTip>
                                                        </>
                                                    )}
                                                    <MatToolTip title={scenario.title} placement="bottom">
                                                        <div
                                                            id={`scenario-${scenario.id}`}
                                                            className="text-[var(--text-secondary)] truncate cursor-pointer min-w-1 max-w-80 py-2"
                                                        >
                                                            {scenario.title}
                                                        </div>
                                                    </MatToolTip>
                                                </div>
                                                <div className="flex items-center">
                                                    {scenario.loading ? (
                                                        <MatToolTip title="Loading..." placement="top">
                                                            <span className="flex items-center cursor-pointer">
                                                                <LoaderCircle size={16} className="text-[var(--text-primary)] animate-spin" />
                                                            </span>
                                                        </MatToolTip>
                                                    ) : (
                                                        <MatToolTip title="Completed" placement="top">
                                                            <span className="flex items-center cursor-pointer">
                                                                <CircleCheckBig size={16} className="text-green-500" />
                                                            </span>
                                                        </MatToolTip>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {hasSelectedCollection && (
                            <div className="mt-4">
                                <hr className="border-[var(--borderColor)]" />
                                <div className="grid grid-cols-1 gap-3">
                                    <div className={`bg-[var(--agent-option)] hover:bg-[var(--hover-bg)] px-2 py-1 rounded-md text-sm font-semibold shadow-md transition-all`}>
                                        <div
                                            className="flex justify-between items-center gap-x-4 cursor-pointer"
                                            onClick={() => handleOpenTab({
                                                icon: <BookCopy size={16} className="text-[var(--primary-color)]" />,
                                                title: message.selected_collection.name,
                                                subtitle: "Swagger Doc",
                                                type: "collection-swagger",
                                                id: message.selected_collection.id,
                                            }, "collection-swagger")}>
                                            <MatToolTip title="Open Swagger" placement="top">
                                                <div
                                                    className="text-[var(--text-primary)] cursor-pointer ml-2"
                                                >
                                                    <SquareArrowOutUpRight size={16} />
                                                </div>
                                            </MatToolTip>
                                            <MatToolTip title={message.selected_collection.name}>
                                                <div
                                                    className="text-[var(--text-secondary)] truncate cursor-pointer min-w-1 max-w-80 py-2"
                                                >
                                                    {message.selected_collection.name}
                                                </div>
                                            </MatToolTip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {hasCurrentTestScenario && (
                            <div className="mt-4">
                                <hr className="border-[var(--borderColor)]" />
                                <div className={`w-full bg-[var(--agent-option)] hover:bg-[var(--hover-bg)] px-2 py-1 rounded-md text-sm font-semibold shadow-md transition-all`}>
                                    <div className="flex items-center gap-2" onClick={() => handleOpenTab({
                                        title: message.current_test_scenario.title,
                                        type: "test-scenario",
                                        id: message.current_test_scenario.test_scenario_id,
                                    }, "test-scenario")}>
                                        <MatToolTip title="Open Test Scenario" placement="bottom">
                                            <div
                                                className="text-[var(--text-primary)] cursor-pointer ml-2"
                                            >
                                                <SquareArrowOutUpRight size={16} />
                                            </div>
                                        </MatToolTip>
                                        <MatToolTip title={message.current_test_scenario.title} placement="bottom">
                                            <div
                                                className="text-[var(--text-secondary)] truncate cursor-pointer min-w-1 max-w-80 py-2"
                                            >
                                                {message.current_test_scenario.title}
                                            </div>
                                        </MatToolTip>
                                    </div>
                                </div>
                            </div>
                        )}
                        {hasCurrentTestCaseSet && (
                            <div className="mt-4">
                                <hr className="border-[var(--borderColor)]" />
                                <div className="grid grid-cols-1 gap-3">
                                    <div className={`bg-[var(--agent-option)] hover:bg-[var(--hover-bg)] px-2 py-1 rounded-md text-sm font-semibold shadow-md transition-all`}>
                                        <div className="flex items-center gap-x-4" onClick={() => handleOpenTab({
                                            title: message.current_test_case_set.title,
                                            type: "test-scenario",
                                            id: message.current_test_case_set.test_scenario_id,
                                            testCaseSetId: message.current_test_case_set.test_case_set_id,
                                        }, "test-scenario")}>
                                            <MatToolTip title="Open Step" placement="bottom">
                                                <div
                                                    className="text-[var(--text-primary)] cursor-pointer ml-2"
                                                >
                                                    <SquareArrowOutUpRight size={16} />
                                                </div>
                                            </MatToolTip>
                                            <MatToolTip title={message.current_test_case_set.title} placement="bottom">
                                                <div
                                                    className="text-[var(--text-secondary)] truncate cursor-pointer min-w-1 max-w-80 py-2"
                                                >
                                                    {message.current_test_case_set.title}
                                                </div>
                                            </MatToolTip>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {hasSuggestions && isLatestMessage && (
                            <div className="mt-4">
                                {/* <hr className="border-[var(--borderColor)]" /> */}
                                <div className="flex flex-wrap gap-3">
                                    {message.suggestions.map((suggestion, index) => {
                                        // Get display text (always use title if available, otherwise use string)
                                        const displayText = typeof suggestion === 'object'
                                            ? (suggestion.title || '')
                                            : suggestion;

                                        // Get click text (use description if available, otherwise use title or string)
                                        const clickText = typeof suggestion === 'object'
                                            ? (suggestion.description || suggestion.title || '')
                                            : suggestion;

                                        return (
                                            <MatToolTip title={isLatestMessage ? "Select Suggestion" : "Newer Messages Present"} placement="bottom">
                                                <div className={`${!isLatestMessage ? "opacity-50 cursor-not-allowed" : ""}`}>
                                                    <Button
                                                        key={index}
                                                        type="button"
                                                        icon={<SquareMousePointer size={14} />}
                                                        onClick={() => isLatestMessage && onSuggestionClick(clickText)}
                                                        disabled={!isLatestMessage}
                                                        className="bgSecondary hover:bg-[var(--hover-bg)] !rounded-md border !border-[var(--borderColor)] min-w-4 w-full p-2 text-[var(--text-primary)] shadow-md transition-all duration-150"
                                                    >
                                                        <span className="pl-2 fs12">{displayText}</span>
                                                    </Button>
                                                </div>
                                            </MatToolTip>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {hasSources && (
                            <div className="mt-4">
                                <span className="text-[var(--text-primary)] font-medium">Sources</span>
                                <div className="relative mt-2">
                                    <div className="flex flex-nowrap gap-3 overflow-x-auto scrollbar-hide" id={`sources-container-${message.id}`}>
                                        {message.sources.map((source, index) => {
                                            const { domain, category, url } = getSourceInfo(source);
                                            return (
                                                <MatToolTip title={<span className="text-sm">{url}</span>} placement="top">
                                                    <div
                                                        key={index}
                                                        onClick={() => window.open(source, '_blank')}
                                                        className="bgSecondary hover:bg-[var(--hover-bg)] rounded-lg border !border-[var(--borderColor)] w-[220px] h-[46px] p-3 cursor-pointer shadow-md transition-all duration-150 flex-shrink-0"
                                                    >
                                                        <div className="flex items-start justify-between h-full">
                                                            <div className="flex items-center gap-2 flex-grow min-w-0">
                                                                <div className="w-6 h-6 rounded-full bgPrimary flex items-center justify-center flex-shrink-0">
                                                                    <img src={qodexLogoFull} alt="Logo" className="w-full h-full object-cover" />
                                                                </div>
                                                                <div className="flex-grow min-w-0">
                                                                    <div className="text-[var(--text-primary)] font-medium text-sm truncate">
                                                                        {domain}
                                                                    </div>
                                                                    <div className="text-[var(--text-secondary)] text-xs truncate capitalize mt-1">
                                                                        {category}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex-shrink-0 ml-2">
                                                                <ExternalLink size={12} className="text-[var(--text-secondary)]" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </MatToolTip>
                                            );
                                        })}
                                    </div>

                                    {/* Navigation buttons */}
                                    <button
                                        onClick={() => {
                                            const container = document.getElementById(`sources-container-${message.id}`);
                                            if (container) {
                                                container.scrollBy({ left: -240, behavior: 'smooth' });
                                            }
                                        }}
                                        className="absolute -left-5 top-1/2 transform -translate-y-1/2 bgPrimary hover:bg-[var(--hover-bg)] border !border-[var(--borderColor)] !rounded-full w-7 h-7 flex items-center justify-center shadow-md transition-all duration-150 z-10"
                                    >
                                        <ChevronLeft size={20} className="text-[var(--text-secondary)]" />
                                    </button>

                                    <button
                                        onClick={() => {
                                            const container = document.getElementById(`sources-container-${message.id}`);
                                            if (container) {
                                                container.scrollBy({ left: 240, behavior: 'smooth' });
                                            }
                                        }}
                                        className="absolute -right-6 top-1/2 transform -translate-y-1/2 bgPrimary hover:bg-[var(--hover-bg)] border !border-[var(--borderColor)] !rounded-full w-7 h-7 flex items-center justify-center shadow-md transition-all duration-150 z-10"
                                    >
                                        <ChevronRight size={20} className="text-[var(--text-secondary)]" />
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}
            {isLoading && (
                <div className="flex justify-start items-center mb-6 animate-fade-in">
                    {/* <Avatar
                        icon={<img src={qodexLogoFull} alt="Qodex AI Assistant" className="object-cover w-full h-full" />}
                        className="mr-3 bg-white text-[var(--text-primary)] shadow-lg animate-bounce"
                        style={{ width: "2.5rem", height: "2.5rem" }}
                        shape="circle"
                    /> */}

                    <div
                        className="max-w-[85%] transition-all duration-300 rounded-md px-3 py-2 
                            bg-transparent text-[var(--text-primary)] relative pb-6 opacity-80 animate-pulse"
                    >
                        <pre className="m-0 p-2 leading-relaxed whitespace-pre-wrap break-words text-sm flex items-center chatbot-thinking-dots">
                            <span className="chatbot-thinking-dot"></span>
                            <span className="chatbot-thinking-dot"></span>
                            <span className="chatbot-thinking-dot"></span>
                        </pre>
                    </div>
                </div>
            )}
            <FeedbackDialog visible={feedBackDialogVisible} onClose={hideFeedbackDialog} selectedMessageId={selectedMessageId} chatId={chatId} feedbackType={feedbackType} />
        </>
    );
};

export default React.memo(DefaultMessageResponse);